import '../fake-db';
import { Provider } from 'react-redux';
import { useRoutes, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { MatxTheme } from './components';
import { AuthProvider } from './contexts/JWTAuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { Store } from './redux/Store';
import AuthGuard from './auth/AuthGuard';
import dashboardRoutes from './views/dashboard/DashboardRoutes';
import settings from './views/Setting/SettingRoutes';
import NotFound from './views/sessions/NotFound';
import sessionRoutes from './views/sessions/SessionRoutes';
import { Navigate } from 'react-router-dom';
import MatxLayout from './components/MatxLayout/MatxLayout';
import servicesRoutes from './views/services/Servicesroutes';
import servicesProviderRoutes from './views/ServiceProvider/ProviderRoutes';
import customerRoutes from './views/customers/CustomerRoutes';
import Booking from './views/booking/Booking';
import CleanerSignup from './views/ServiceProvider/CRUD/CleanerSignup';
import PayrollRoutes from './views/payRoll/PayrollRoutes';

import bookingRoutes from './views/bookingOverview/BookingRoutes';
import dispatcherRoutes from './views/dispatcher/dispatcherRoutes';
import customerViewRoutes from './views/CustomerView/customerViewRoutes';
import ReportsRoutes from './views/Reports/ReportsRoutes';
import CustomerBooking from './views/CustomerView/customerBooking';
import managerRoutes from './views/Managers/CustomerRoutes';
import calendarRoutes from './views/calendar/calendarRoutes';
import emailTemplateRoutes from './views/EmailTemplating/templateRoutes';
import chatRoutes from './views/publicChat/chatRoutes';
import frequencyRoutes from './views/frequency/frequencyRoutes';
import dealsRoutes from './views/deals/dealsRoutes';
import promoRoutes from './views/promo/promoRoutes';
import giftCardRoutes from './views/giftCards/giftCardsRoutes';
import { ConfirmProvider } from 'material-ui-confirm';
import RequestReview from './views/requestReview/requestReview';
import { ChargeTip } from './views/chargeTip/chargeTip';
import CustomerResetPassword from './views/customer-reset-password';
import ConfirmBooking from './views/CustomerView/confirmBooking';
import emailTemplatesRoutes from './views/EmailTemplates/templateRoutes';
import sendEmailRoutes from './views/SendEmail/sendEmailRoutes';
import ZelleCheckout from './views/CustomerView/zelleCheckout';
import usersSubscribeRoutes from './views/users-subscription/CustomerRoutes';
import 'react-phone-input-2/lib/material.css';
import TermsAndConditionsRoutes from './views/termsAndConditions/termsAndConditionsRoutes';
import PrivacyPolicyRoutes from './views/privacyPolicy/privacyPolicyRoutes';
import sitemapRoutes from './views/sitemap/sitemapRoutes';
import { NotificationProvider } from './contexts/NotificationContext';
import PlatformGuard from './views/superAdmin/PlatformGuard';
import PlatformPanel from './views/superAdmin/PlatformPanel';
import CommunicationSettings from './views/superAdmin/CommunicationSettings';
import PublicServices from './views/services/PublicServices';
import JwtRegister from './views/sessions/JwtRegister';
import NewProfile from './views/sessions/Profile';
import CompanyDiscovery from './views/services/CompanyDiscovery';
import LegalPolicyPublicPage from './views/legalPolicies/LegalPolicyPublicPage';
import TenantAdminSetPassword from './views/sessions/TenantAdminSetPassword';

const App = () => {
  const token = localStorage.getItem('accessToken');
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const urlEmail = searchParams.get("email");
  const content = useRoutes([
    { path: '/super-admin/login', element: <Navigate replace to="/login" /> },
    {
      path: '/super-admin',
      element: <PlatformGuard><MatxLayout /></PlatformGuard>,
      children: [
        { index: true, element: <Navigate replace to="dashboard" /> },
        { path: 'communication-settings', element: <CommunicationSettings /> },
        ...['dashboard', 'companies', 'subscriptions', 'payments', 'issues', 'leaderboard'].map((path) => ({ path, element: <PlatformPanel /> })),
      ],
    },
    {
      element: (
        <AuthGuard>
          <MatxLayout />
        </AuthGuard>
      ),
      children: [
        ...dashboardRoutes,
        ...settings,
        ...servicesRoutes,
        ...servicesProviderRoutes,
        ...PayrollRoutes,
        ...customerRoutes,
        ...usersSubscribeRoutes,
        ...bookingRoutes,
        ...dispatcherRoutes,
        ...customerViewRoutes,
        ...ReportsRoutes,
        ...managerRoutes,
        ...calendarRoutes,
        ...chatRoutes,
        ...emailTemplateRoutes,
        ...sendEmailRoutes,
        ...emailTemplatesRoutes,
        ...frequencyRoutes,
        ...dealsRoutes,
        ...promoRoutes,
        ...giftCardRoutes,
        ...TermsAndConditionsRoutes,
        ...PrivacyPolicyRoutes,
        ...sitemapRoutes,
        // ...accessRightsRoutes,
      ],
    },
    { path: '/request-review', element: <RequestReview /> },
    { path: '/charge-tip', element: <ChargeTip /> },
    { path: '/customer-reset-password', element: <CustomerResetPassword /> },
    { path: '/set-password', element: <TenantAdminSetPassword /> },
    { path: '/provider/:provider/services', element: <PublicServices /> },
    { path: '/services', element: <CompanyDiscovery /> },
    { path: '/services/provider/:provider', element: <PublicServices /> },
    { path: '/provider/:provider/booking/:slug', element: <Booking /> },
    { path: '/provider/:provider/confirm-booking', element: <ConfirmBooking /> },
    { path: '/provider/:provider/zelle-checkout', element: <ZelleCheckout /> },
    { path: '/provider/:provider/signup', element: <JwtRegister /> },
    { path: '/provider/:provider/profile', element: <NewProfile /> },
    { path: '/provider/:provider/privacy-policy', element: <LegalPolicyPublicPage policyType="privacy" /> },
    { path: '/provider/:provider/terms-and-conditions', element: <LegalPolicyPublicPage policyType="terms" /> },
    { path: '/booking/provider/:provider/:slug', element: <Booking /> },
    { path: '/booking/:slug', element: <Booking /> },
    { path: '/cleaner-signup', element: <CleanerSignup /> },
    { path: '/customerbook/:slug', element: <CustomerBooking /> },
    {
      path: '/',

      element: !token || (pathname?.includes('forgot-password') && urlEmail) ? <Outlet /> : <Navigate replace to="/dashboard/default" />,
      children: [...sessionRoutes],
    },
    ...sessionRoutes,
    { path: '*', element: <NotFound /> },
    { path: '/confirmBooking', element: <ConfirmBooking /> },
    { path: '/zelleCheckout', element: <ZelleCheckout /> },
  ]);

  return (
    <Provider store={Store}>
      <SettingsProvider>
        <MatxTheme>
          <AuthProvider>
            <NotificationProvider>
              <ConfirmProvider>
                {content}
              </ConfirmProvider>
            </NotificationProvider>
          </AuthProvider>
        </MatxTheme>
      </SettingsProvider>
    </Provider>
  );
};

export default App;
